import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { 
  Calendar, 
  Clock, 
  User, 
  MessageSquare,
  AlertCircle,
  CheckCircle2,
  PauseCircle,
  XCircle,
  Send,
  UserCheck,
  Trash2,
  Edit2
} from 'lucide-react';
import { tasksApi, Comment, usersApi } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { LoadingSpinner } from '../components/LoadingScreen';
import { SpeechToText } from '../components/SpeechToText';
import { isTaskOverdue } from '../utils/overdue';
import { EditTaskModal } from '../components/EditTaskModal';
import { useTelegram } from '../hooks/useTelegram';
import { useAuthStore } from '../store/auth';
import { useLogger } from '../utils/logger';

const statusConfig = {
  CREATED: { label: 'Задача поставлена', icon: Clock, variant: 'default' as const, color: 'bg-gray-500' },
  IN_PROGRESS: { label: 'В процессе', icon: Clock, variant: 'info' as const, color: 'bg-blue-500' },
  PENDING_APPROVAL: { label: 'Ожидает подтверждения', icon: Clock, variant: 'warning' as const, color: 'bg-yellow-500' },
  COMPLETED: { label: 'Выполнена', icon: CheckCircle2, variant: 'success' as const, color: 'bg-green-500' },
  PAUSED: { label: 'Приостановлена', icon: PauseCircle, variant: 'warning' as const, color: 'bg-yellow-500' },
  FAILED: { label: 'Не выполнена', icon: XCircle, variant: 'danger' as const, color: 'bg-red-500' },
  NOT_SELECTED: { label: 'Статус не выбран', icon: Clock, variant: 'default' as const, color: 'bg-gray-500' },
};

const priorityConfig = {
  CRITICAL: { label: 'Сверхважная', color: 'text-red-500', bg: 'bg-red-100' },
  HIGH: { label: 'Важная', color: 'text-orange-500', bg: 'bg-orange-100' },
  CURRENT: { label: 'Текущая', color: 'text-green-500', bg: 'bg-green-100' },
};

export function TaskPage() {
  const logger = useLogger('TaskPage');
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hapticFeedback, showAlert, showConfirm } = useTelegram();
  const currentUser = useAuthStore((state) => state.user);

  const [activeCommentTab, setActiveCommentTab] = useState<'EXECUTION' | 'MANAGEMENT'>('EXECUTION');
  const [newComment, setNewComment] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Проверяем, является ли пользователь системным администратором
  const { data: currentUserData } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: () => usersApi.me(),
    enabled: true,
  });

  const isSystemAdmin = Boolean((currentUserData as any)?.isSystemAdmin);

  useEffect(() => {
    logger.componentMount({ taskId });
    return () => {
      logger.componentUnmount();
    };
  }, [taskId]);

  const { data: task, isLoading, error } = useQuery({
    queryKey: ['task', taskId],
    queryFn: async () => {
      logger.apiCall('GET', `/tasks/${taskId}`);
      try {
        const data = await tasksApi.get(taskId!);
        logger.apiSuccess('GET', `/tasks/${taskId}`, data);
        return data;
      } catch (error: any) {
        logger.apiError('GET', `/tasks/${taskId}`, error);
        throw error;
      }
    },
    enabled: !!taskId,
    retry: (failureCount, error: any) => {
      // Не повторяем запрос при 404 (задача не найдена)
      if (error?.status === 404) {
        logger.warn('Retry skipped due to 404', { taskId });
        return false;
      }
      logger.debug(`Retry attempt ${failureCount + 1}`, { failureCount });
      return failureCount < 2;
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async (status: string) => {
      console.log('[UPDATE STATUS MUTATION] Starting mutation', { taskId, status });
      logger.apiCall('PATCH', `/tasks/${taskId}`, { status });
      try {
        console.log('[UPDATE STATUS MUTATION] Calling tasksApi.update', { taskId, status });
        const result = await tasksApi.update(taskId!, { status });
        console.log('[UPDATE STATUS MUTATION] API call succeeded', result);
        logger.apiSuccess('PATCH', `/tasks/${taskId}`, result);
        return result;
      } catch (error: any) {
        console.error('[UPDATE STATUS MUTATION] API call failed', {
          error,
          message: error?.message,
          status: error?.status,
          taskId,
          statusToUpdate: status,
        });
        logger.apiError('PATCH', `/tasks/${taskId}`, error);
        throw error;
      }
    },
    onMutate: async (newStatus) => {
      // Отменяем исходящие запросы, чтобы они не перезаписали оптимистичное обновление
      await queryClient.cancelQueries({ queryKey: ['task', taskId] });

      // Сохраняем предыдущее значение для отката
      const previousTask = queryClient.getQueryData(['task', taskId]);

      // Оптимистично обновляем данные
      queryClient.setQueryData(['task', taskId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          status: newStatus,
          updatedAt: new Date().toISOString(),
        };
      });

      return { previousTask };
    },
    onError: (err: any, _newStatus, context) => {
      // Откатываем изменения при ошибке
      if (context?.previousTask) {
        queryClient.setQueryData(['task', taskId], context.previousTask);
      }
      logger.error('Task status update failed', err, { taskId, errorDetails: err });
      const errorMessage = err?.message || err?.error || 'Не удалось обновить статус задачи';
      console.error('[TASK STATUS UPDATE] Error details:', {
        error: err,
        message: errorMessage,
        status: err?.status,
        taskId,
      });
      showAlert(`Ошибка: ${errorMessage}`);
      hapticFeedback('error');
    },
    onSuccess: async (data, status) => {
      logger.userAction('task_status_updated', { taskId, status });
      console.log('[UPDATE STATUS MUTATION] Success, updating cache', { taskId, status, data });
      
      // Обновляем данные в кэше с актуальными данными с сервера
      queryClient.setQueryData(['task', taskId], data);
      await queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      
      // Инвалидируем все связанные запросы задач для обновления списков
      await queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      
      hapticFeedback('success');
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: (data: { type: 'EXECUTION' | 'MANAGEMENT'; text: string }) =>
      tasksApi.addComment(taskId!, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      await queryClient.refetchQueries({ queryKey: ['task', taskId] });
      setNewComment('');
      hapticFeedback('success');
    },
    onError: (err: Error) => {
      showAlert(`Ошибка: ${err.message}`);
      hapticFeedback('error');
    },
  });

  const acceptTaskMutation = useMutation({
    mutationFn: () => tasksApi.accept(taskId!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      await queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      await queryClient.refetchQueries({ queryKey: ['task', taskId] });
      hapticFeedback('success');
      showAlert('Задача принята!');
    },
    onError: (err: any) => {
      const errorMessage = err?.message || 'Не удалось принять задачу';
      showAlert(`Ошибка: ${errorMessage}`);
      hapticFeedback('error');
    },
  });

  const approveTaskMutation = useMutation({
    mutationFn: () => tasksApi.approve(taskId!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      await queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['protocols'] });
      await queryClient.refetchQueries({ queryKey: ['task', taskId] });
      hapticFeedback('success');
      showAlert('Задача подтверждена!');
    },
    onError: (err: any) => {
      const errorMessage = err?.message || 'Не удалось подтвердить задачу';
      showAlert(`Ошибка: ${errorMessage}`);
      hapticFeedback('error');
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: () => tasksApi.delete(taskId!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      await queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['protocol', task?.section?.protocol?.id] });
      hapticFeedback('success');
      showAlert('Задача удалена');
      navigate(-1);
    },
    onError: (err: Error) => {
      showAlert(`Ошибка: ${err.message}`);
      hapticFeedback('error');
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: (data: any) => tasksApi.update(taskId!, data),
    onSuccess: async (data) => {
      queryClient.setQueryData(['task', taskId], data);
      await queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      await queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      await queryClient.refetchQueries({ queryKey: ['task', taskId] });
      setIsEditModalOpen(false);
      hapticFeedback('success');
      showAlert('Задача обновлена');
    },
    onError: (err: Error) => {
      showAlert(`Ошибка: ${err.message}`);
      hapticFeedback('error');
    },
  });

  const handleDelete = async () => {
    const confirmed = await showConfirm('Удалить задачу? Это действие необратимо.');
    if (confirmed) {
      deleteTaskMutation.mutate();
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    console.log('[TASK STATUS CHANGE] Starting status change', {
      taskId,
      currentStatus: task?.status,
      newStatus,
      taskExists: !!task,
    });
    
    if (task?.status === newStatus) {
      console.log('[TASK STATUS CHANGE] Status unchanged, skipping');
      return;
    }

    if (newStatus === 'COMPLETED') {
      const confirmed = await showConfirm('Отметить задачу как выполненную?');
      if (!confirmed) {
        console.log('[TASK STATUS CHANGE] User cancelled completion confirmation');
        return;
      }
    }

    console.log('[TASK STATUS CHANGE] Calling mutation with status:', newStatus);
    hapticFeedback('medium');
    
    try {
      updateStatusMutation.mutate(newStatus, {
        onSuccess: (data) => {
          console.log('[TASK STATUS CHANGE] Mutation succeeded', data);
        },
        onError: (error) => {
          console.error('[TASK STATUS CHANGE] Mutation error', error);
        },
      });
    } catch (error) {
      console.error('[TASK STATUS CHANGE] Error calling mutation', error);
      showAlert(`Ошибка: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
    }
  };

  const handleAddComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    addCommentMutation.mutate({
      type: activeCommentTab,
      text: newComment.trim(),
    });
  };

  const handleSpeechResult = (text: string) => {
    setNewComment((prev) => {
      const trimmed = prev.trim();
      return trimmed ? `${trimmed} ${text}` : text;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    const errorMessage = (error as any)?.message || 'Не удалось загрузить задачу';
    const isNotFound = (error as any)?.status === 404;
    
    return (
      <div className="p-4 text-center">
        <p className="text-tg-hint mb-4">
          {isNotFound ? 'Задача не найдена' : errorMessage}
        </p>
        <Button onClick={() => navigate(-1)}>Назад</Button>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const status = statusConfig[task.status];
  const priority = priorityConfig[task.priority];
  const isOverdue = isTaskOverdue(task);

  const isAdmin = task.userRole === 'ADMIN';
  // Системный администратор может редактировать любые задачи
  const canEdit = isSystemAdmin || (task.userRole && ['ADMIN', 'MANAGER'].includes(task.userRole));
  const isCreator = task.createdBy && currentUser && task.createdBy.id === currentUser.id;
  const canApprove = (isCreator || canEdit) && task.status === 'PENDING_APPROVAL';
  
  const canAddExecComment = ['ADMIN', 'MANAGER', 'EXECUTOR'].includes(task.userRole);
  const canAddMgmtComment = ['ADMIN', 'MANAGER'].includes(task.userRole);
  const isAssignee = Boolean(currentUser && task.assignees.some((a) => a.id === currentUser.id));
  const canChangeStatus = canEdit || isCreator || isAssignee;

  const executionComments = task.comments.filter(c => c.type === 'EXECUTION');
  const managementComments = task.comments.filter(c => c.type === 'MANAGEMENT');

  return (
    <div className="pb-20">
      {/* Заголовок */}
      <div className="p-4 bg-tg-secondary-bg">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-2xl font-bold text-tg-text">{task.code}</span>
          <Badge variant={status.variant}>{status.label}</Badge>
        </div>
        
        <div className="flex items-center gap-2 mb-3">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${priority.bg} ${priority.color}`}>
            {priority.label}
          </span>
          {isOverdue && (
            <span className="flex items-center gap-1 text-red-500 text-xs font-medium">
              <AlertCircle className="w-3.5 h-3.5" />
              Просрочено
            </span>
          )}
        </div>

        <p className="text-tg-text text-sm">
          {task.section.number}. {task.section.title}
        </p>
      </div>

      {/* Текст задачи */}
      <Card className="m-4 p-4">
        <div className="flex items-start justify-between gap-3">
          <p className="text-tg-text whitespace-pre-wrap flex-1">{task.text}</p>
          {canEdit && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsEditModalOpen(true)}
              className="flex-shrink-0"
            >
              <Edit2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </Card>

      {/* Даты */}
      <Card className="mx-4 mb-4 p-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-tg-hint mb-1">Дата начала</p>
            <p className="text-sm text-tg-text flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {task.startDate 
                ? format(new Date(task.startDate), 'd MMMM yyyy', { locale: ru })
                : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-tg-hint mb-1">Дедлайн</p>
            <p className={`text-sm flex items-center gap-1 ${isOverdue ? 'text-red-500 font-medium' : 'text-tg-text'}`}>
              <Calendar className="w-4 h-4" />
              {task.dueDate 
                ? format(new Date(task.dueDate), 'd MMMM yyyy', { locale: ru })
                : '—'}
            </p>
          </div>
        </div>
        {task.closedAt && task.status === 'COMPLETED' && (
          <div className="mt-3 pt-3 border-t border-tg-bg">
            <p className="text-xs text-tg-hint mb-1">Дата выполнения</p>
            <p className="text-sm text-green-600 flex items-center gap-1 font-medium">
              <CheckCircle2 className="w-4 h-4" />
              {format(new Date(task.closedAt), 'd MMMM yyyy, HH:mm', { locale: ru })}
            </p>
          </div>
        )}
      </Card>

      {/* Ответственные */}
      {task.assignees.length > 0 && (
        <Card className="mx-4 mb-4 p-4">
          <p className="text-xs text-tg-hint mb-2">Ответственные</p>
          <div className="space-y-2">
            {task.assignees.map((assignee) => {
              const isCurrentUser = currentUser && assignee.id === currentUser.id;
              const canAccept = isCurrentUser && !assignee.accepted;
              
              return (
                <div key={assignee.id} className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-tg-button/20 flex items-center justify-center">
                    <User className="w-4 h-4 text-tg-button" />
                  </div>
                  <div>
                    <p className="text-sm text-tg-text">
                      {assignee.lastName || ''} {assignee.firstName}
                    </p>
                    {assignee.username && (
                      <p className="text-xs text-tg-hint">@{assignee.username}</p>
                    )}
                  </div>
                  {assignee.accepted ? (
                    <Badge size="sm" variant="success" className="ml-auto">
                      <UserCheck className="w-3 h-3 mr-1" />
                      Принял
                    </Badge>
                  ) : canAccept ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="ml-auto"
                      onClick={(e) => {
                        e.stopPropagation();
                        acceptTaskMutation.mutate();
                      }}
                      isLoading={acceptTaskMutation.isPending}
                    >
                      Принять
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Кнопка подтверждения для постановщика */}
      {canApprove && (
        <Card className="mx-4 mb-4 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-tg-text mb-1">
                Задача ожидает подтверждения
              </p>
              <p className="text-xs text-tg-hint">
                Исполнитель отметил задачу как выполненную. Подтвердите выполнение.
              </p>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                hapticFeedback('medium');
                approveTaskMutation.mutate();
              }}
              isLoading={approveTaskMutation.isPending}
              className="ml-4"
            >
              ✅ Принято
            </Button>
          </div>
        </Card>
      )}

      {/* Быстрая смена статуса */}
      {canChangeStatus && (
        <Card className="mx-4 mb-4 p-4">
          <p className="text-xs text-tg-hint mb-3">Изменить статус</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { status: 'IN_PROGRESS', label: '🔄 В процессе' },
              { status: 'COMPLETED', label: '✅ Выполнено' },
              { status: 'PAUSED', label: '⏸ Приостановлено' },
              { status: 'FAILED', label: '❌ Не выполнено' },
            ].map((item) => (
              <Button
                key={item.status}
                variant={task.status === item.status ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => handleStatusChange(item.status)}
                disabled={updateStatusMutation.isPending}
              >
                {item.label}
              </Button>
            ))}
          </div>
        </Card>
      )}

      {/* Комментарии */}
      <Card className="mx-4 mb-4">
        {/* Табы */}
        <div className="flex border-b border-tg-bg">
          <button
            onClick={() => setActiveCommentTab('EXECUTION')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeCommentTab === 'EXECUTION'
                ? 'text-tg-button border-b-2 border-tg-button'
                : 'text-tg-hint'
            }`}
          >
            <MessageSquare className="w-4 h-4 inline mr-1" />
            Исполнение ({executionComments.length})
          </button>
          <button
            onClick={() => setActiveCommentTab('MANAGEMENT')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeCommentTab === 'MANAGEMENT'
                ? 'text-tg-button border-b-2 border-tg-button'
                : 'text-tg-hint'
            }`}
          >
            <MessageSquare className="w-4 h-4 inline mr-1" />
            Руководство ({managementComments.length})
          </button>
        </div>

        {/* Список комментариев */}
        <div className="p-4 space-y-3 max-h-60 overflow-auto">
          {(activeCommentTab === 'EXECUTION' ? executionComments : managementComments).length === 0 ? (
            <p className="text-sm text-tg-hint text-center py-4">
              Нет комментариев
            </p>
          ) : (
            (activeCommentTab === 'EXECUTION' ? executionComments : managementComments).map((comment) => (
              <CommentItem key={comment.id} comment={comment} />
            ))
          )}
        </div>

        {/* Форма добавления */}
        {((activeCommentTab === 'EXECUTION' && canAddExecComment) ||
          (activeCommentTab === 'MANAGEMENT' && canAddMgmtComment)) && (
          <form onSubmit={handleAddComment} className="p-4 border-t border-tg-bg">
            <div className="flex items-center gap-2 mb-2">
              <label className="text-xs text-tg-hint flex-1">Написать комментарий</label>
              <SpeechToText onResult={handleSpeechResult} disabled={addCommentMutation.isPending} />
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Введите или продиктуйте комментарий..."
                className="flex-1 px-4 py-2 rounded-xl bg-tg-bg text-tg-text placeholder:text-tg-hint text-sm focus:outline-none"
              />
              <Button
                type="submit"
                size="sm"
                disabled={!newComment.trim() || addCommentMutation.isPending}
                isLoading={addCommentMutation.isPending}
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
        )}
      </Card>

      {/* Кнопка удаления (только админ или системный администратор) */}
      {(isAdmin || isSystemAdmin) && (
        <Card className="mx-4 mb-4 p-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDelete}
            disabled={deleteTaskMutation.isPending}
            isLoading={deleteTaskMutation.isPending}
            className="w-full gap-1 text-red-500 border-red-200 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
            Удалить задачу
          </Button>
        </Card>
      )}

      {/* Мета информация */}
      <div className="mx-4 text-xs text-tg-hint">
        <p>Создано: {format(new Date(task.createdAt), 'd MMMM yyyy, HH:mm', { locale: ru })}</p>
        {task.createdBy && (
          <p>Автор: {task.createdBy.firstName} {task.createdBy.lastName || ''}</p>
        )}
      </div>

      {/* Модальное окно редактирования */}
      {isEditModalOpen && (
        <EditTaskModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          task={task}
          onSave={updateTaskMutation.mutate}
          isSaving={updateTaskMutation.isPending}
        />
      )}
    </div>
  );
}

function CommentItem({ comment }: { comment: Comment }) {
  // Форматируем дату в формате ДД.ММ.ГГГГ
  const formattedDate = format(new Date(comment.createdAt), 'dd.MM.yyyy', { locale: ru });
  const authorName = `${comment.user.firstName} ${comment.user.lastName || ''}`.trim();
  
  return (
    <div className="bg-tg-bg rounded-xl p-3">
      <p className="text-sm text-tg-text whitespace-pre-wrap">
        <span className="text-tg-hint font-medium">{formattedDate}</span>
        {' / '}
        <span className="text-tg-text">{comment.text}</span>
      </p>
      <p className="text-xs text-tg-hint mt-1">
        {authorName}
      </p>
    </div>
  );
}

